import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Receipt as ReceiptIcon } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { EmptyState, Pagination, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { collectionsApi } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { formatCents } from '@/domain/money';
import { addDays, formatDateTime, today } from '@/lib/dateUtils';
import { useAuthStore } from '@/app/store/authStore';

export function CollectionsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const [from, setFrom] = useState(addDays(today(), -7));
  const [to, setTo] = useState(today());
  const [page, setPage] = useState(1);

  const params = { from, to, page, pageSize: 25, status: 'all' as const };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.collections.list(params),
    queryFn: () => collectionsApi.list(params),
    placeholderData: (previous) => previous,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={user?.role === 'AGENT' ? t('nav.myCollections') : t('collection.title')}
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4">
          <Input
            label={t('common.from')}
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
            className="w-auto"
          />
          <Input
            label={t('common.to')}
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
            className="w-auto"
          />
        </div>

        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={<ReceiptIcon className="h-6 w-6" />} title={t('collection.empty')} />
        ) : (
          <>
            {/* Card list below sm: an 8-column table would force horizontal
                scrolling on the phones agents actually use this screen on. */}
            <div className="divide-y divide-slate-100 sm:hidden">
              {data.rows.map((receipt) => (
                <button
                  key={receipt.id}
                  type="button"
                  onClick={() => navigate(`/collections/${receipt.id}`)}
                  className="flex w-full flex-col gap-1.5 px-4 py-3 text-left active:bg-slate-50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="tabular font-semibold text-slate-900">{receipt.receiptNo}</span>
                    {receipt.status === 'VOID' ? (
                      <Badge tone="danger">{t('collection.voidedBadge')}</Badge>
                    ) : (
                      <Badge tone="success">{t('common.active')}</Badge>
                    )}
                  </div>
                  <p className="truncate text-sm text-slate-700">{receipt.shopName}</p>
                  <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span>{formatDateTime(receipt.collectedAt)}</span>
                    <span className="truncate">{receipt.agentName}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span>
                      {receipt.cashCents > 0 && `${t('collection.cash')} ${formatCents(receipt.cashCents)}`}
                      {receipt.cashCents > 0 && receipt.chequeCents > 0 && ' · '}
                      {receipt.chequeCents > 0 &&
                        `${t('collection.cheque')} ${formatCents(receipt.chequeCents)}`}
                    </span>
                    <span className="tabular text-sm font-semibold text-slate-900">
                      {formatCents(receipt.totalCents)}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="hidden sm:block">
              <Table>
                <THead>
                  <TR>
                    <TH>{t('collection.receiptNo')}</TH>
                    <TH>{t('collection.collectedAt')}</TH>
                    <TH>{t('collection.customer')}</TH>
                    <TH>{t('collection.agent')}</TH>
                    <TH align="right">{t('collection.cash')}</TH>
                    <TH align="right">{t('collection.cheque')}</TH>
                    <TH align="right">{t('common.total')}</TH>
                    <TH align="center">{t('common.status')}</TH>
                  </TR>
                </THead>

                <TBody>
                  {data.rows.map((receipt) => (
                    <TR key={receipt.id} onClick={() => navigate(`/collections/${receipt.id}`)}>
                      <TD className="tabular font-semibold text-slate-900">{receipt.receiptNo}</TD>
                      <TD className="text-xs text-slate-500">{formatDateTime(receipt.collectedAt)}</TD>
                      <TD>{receipt.shopName}</TD>
                      <TD className="text-slate-500">{receipt.agentName}</TD>
                      <TD align="right" numeric>
                        {receipt.cashCents > 0 ? formatCents(receipt.cashCents) : '—'}
                      </TD>
                      <TD align="right" numeric>
                        {receipt.chequeCents > 0 ? formatCents(receipt.chequeCents) : '—'}
                      </TD>
                      <TD align="right" numeric className="font-semibold text-slate-900">
                        {formatCents(receipt.totalCents)}
                      </TD>
                      <TD align="center">
                        {receipt.status === 'VOID' ? (
                          <Badge tone="danger">{t('collection.voidedBadge')}</Badge>
                        ) : (
                          <Badge tone="success">{t('common.active')}</Badge>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
